import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  KeyRound, ShieldCheck, ShieldAlert, Loader2, RefreshCw, Trash2, Eye, EyeOff,
  AlertTriangle, CheckCircle2, XCircle,
} from "lucide-react";

/**
 * Admin → Secrets page.
 *
 * Why this page exists
 * --------------------
 * Production env vars on the Emergent platform are normally edited via the
 * deployment settings panel. When that panel is inaccessible, the only
 * way to update Stripe keys / webhook secrets is via the admin override
 * endpoint we added (`/api/admin/secrets/override`). This page is a
 * password-masked UI for that endpoint so admins never have to paste
 * secrets into the browser console (where they'd be logged to history).
 *
 * Security notes
 * --------------
 * • All inputs are `type="password"` — Chrome / Firefox / Safari do NOT
 *   record password-field values in autofill, history, or DevTools state.
 * • The backend NEVER returns raw values; only fingerprints (prefix + suffix
 *   + length). So if an admin token leaks, the attacker can re-set keys but
 *   can't exfiltrate existing ones.
 * • The Stripe-health card surfaces real-time auth status against Stripe
 *   itself, so admins know the moment they paste a working key.
 */

const KEY_LABELS = {
  STRIPE_API_KEY: {
    title: "Stripe API key — Test mode",
    hint: "Used by preview / non-production hosts. Starts with sk_test_ (~107 chars).",
    placeholder: "sk_test_...",
  },
  STRIPE_API_KEY_LIVE: {
    title: "Stripe API key — Live mode",
    hint: "Used by slides.wokelo.ai (production). Starts with sk_live_ or rk_live_ (~107 chars).",
    placeholder: "rk_live_... or sk_live_...",
  },
  STRIPE_WEBHOOK_SECRET: {
    title: "Stripe webhook signing secret — Test",
    hint: "Verifies webhook payloads from Stripe in test mode. Starts with whsec_.",
    placeholder: "whsec_...",
  },
  STRIPE_WEBHOOK_SECRET_LIVE: {
    title: "Stripe webhook signing secret — Live",
    hint: "Verifies webhook payloads from Stripe in production. Starts with whsec_.",
    placeholder: "whsec_...",
  },
};

export default function AdminSecretsPage() {
  const [overrides, setOverrides] = useState([]);
  const [allowedKeys, setAllowedKeys] = useState([]);
  const [stripeHealth, setStripeHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API}/admin/secrets/status`, { withCredentials: true }),
        axios.get(`${API}/admin/stripe-health`, { withCredentials: true }),
      ]);
      setOverrides(s.data?.overrides || []);
      setAllowedKeys(s.data?.allowed_keys || []);
      setStripeHealth(h.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load secret status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const refreshHealth = async () => {
    setHealthLoading(true);
    try {
      const r = await axios.get(`${API}/admin/stripe-health`, { withCredentials: true });
      setStripeHealth(r.data || null);
    } catch (e) {
      toast.error("Stripe health check failed");
    } finally {
      setHealthLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const overrideByKey = Object.fromEntries(overrides.map(o => [o.key, o]));

  return (
    <div className="page-container max-w-5xl" data-testid="admin-secrets-page">
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
        <span>Admin</span><span>/</span><span className="text-gray-900">Secrets</span>
      </div>
      <h1 className="text-2xl font-bold font-['Manrope'] mb-1">Production Secrets</h1>
      <p className="text-sm text-gray-500 mb-5 max-w-3xl">
        Emergency-hatch overrides for Stripe credentials when the Emergent
        deployment-settings panel is inaccessible. Values are stored in
        MongoDB and take effect on the very next API call — no restart, no
        redeploy. Backend checks DB overrides first, then falls back to
        environment variables.
      </p>

      <StripeHealthCard health={stripeHealth} loading={healthLoading} onRefresh={refreshHealth} />

      <div className="space-y-4 mt-6">
        {allowedKeys.map(k => (
          <SecretRow
            key={k}
            keyName={k}
            current={overrideByKey[k]}
            onChange={loadAll}
          />
        ))}
      </div>

      <div className="mt-8 p-4 rounded-md border border-amber-200 bg-amber-50/60">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-900 leading-relaxed">
            <p className="font-semibold mb-1">Security reminders</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>This page is admin-only (enforced server-side).</li>
              <li>Existing values are NEVER displayed — only a {`{prefix}…{suffix}`} fingerprint.</li>
              <li>Inputs are password-masked, so values never enter browser history, autofill, or DevTools state.</li>
              <li>Rotate Stripe keys at least every 90 days. Use this page to update — no deploy required.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function StripeHealthCard({ health, loading, onRefresh }) {
  if (!health) return null;
  return (
    <Card className="border-gray-200 shadow-none mb-5" data-testid="stripe-health-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-gray-500" />
            Stripe live status
          </CardTitle>
          <button
            onClick={onRefresh}
            disabled={loading}
            className="text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1.5"
            data-testid="refresh-stripe-health"
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh
          </button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <KeyHealthChip label="Test key (preview hosts)" probe={health.test} />
          <KeyHealthChip label="Live key (slides.wokelo.ai)" probe={health.live} />
        </div>
        <div className="mt-3 text-xs text-gray-500">
          Webhook secrets configured:&nbsp;
          <span className={health.webhook_secrets_configured?.STRIPE_WEBHOOK_SECRET ? "text-green-700 font-medium" : "text-red-700 font-medium"}>
            test {health.webhook_secrets_configured?.STRIPE_WEBHOOK_SECRET ? "✓" : "✗"}
          </span>
          &nbsp;&middot;&nbsp;
          <span className={health.webhook_secrets_configured?.STRIPE_WEBHOOK_SECRET_LIVE ? "text-green-700 font-medium" : "text-red-700 font-medium"}>
            live {health.webhook_secrets_configured?.STRIPE_WEBHOOK_SECRET_LIVE ? "✓" : "✗"}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function KeyHealthChip({ label, probe }) {
  if (!probe) return null;
  const ok = probe.status === "ok";
  const color = ok ? "border-green-200 bg-green-50" : (probe.status === "missing" ? "border-gray-200 bg-gray-50" : "border-red-200 bg-red-50");
  const Icon = ok ? CheckCircle2 : (probe.status === "missing" ? ShieldAlert : XCircle);
  const iconColor = ok ? "text-green-600" : (probe.status === "missing" ? "text-gray-400" : "text-red-600");

  return (
    <div className={`rounded-md border p-3 ${color}`} data-testid={`health-${probe.key}`}>
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 ${iconColor} flex-shrink-0 mt-0.5`} />
        <div className="min-w-0 flex-1">
          <p className="text-xs text-gray-500 mb-0.5">{label}</p>
          <p className="text-sm font-semibold text-gray-900 capitalize">{probe.status.replace("_", " ")}</p>
          <div className="text-[10px] text-gray-500 mt-1 space-x-2">
            {probe.source && <span>source: <span className="font-mono">{probe.source}</span></span>}
            {probe.account_id && <span>{probe.account_id}</span>}
            {probe.charges_enabled !== undefined && (
              <span className={probe.charges_enabled ? "text-green-700" : "text-red-700"}>
                {probe.charges_enabled ? "charges enabled" : "charges disabled"}
              </span>
            )}
          </div>
          {!ok && probe.message && (
            <p className="text-[11px] text-red-700 mt-1 break-words leading-relaxed">{probe.message}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function SecretRow({ keyName, current, onChange }) {
  const [value, setValue] = useState("");
  const [showValue, setShowValue] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const meta = KEY_LABELS[keyName] || { title: keyName, hint: "", placeholder: "" };
  const overridden = current?.overridden;

  const handleSave = async () => {
    if (!value.trim()) {
      toast.error("Paste the new value before saving.");
      return;
    }
    if (value.trim().length < 20) {
      toast.error("Value looks too short. Did you copy the entire key?");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/secrets/override`,
        { key: keyName, value: value.trim() },
        { withCredentials: true },
      );
      toast.success(`${keyName} override saved.`);
      setValue("");
      setShowValue(false);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save override");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Remove the override for ${keyName}? Backend will fall back to the environment variable.`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/secrets/override/${keyName}`, { withCredentials: true });
      toast.success(`${keyName} override removed.`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to remove override");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card className="border-gray-200 shadow-none" data-testid={`secret-row-${keyName}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">{meta.title}</h3>
              <span className="text-[10px] font-mono text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
                {keyName}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">{meta.hint}</p>
          </div>
          {overridden && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-1 bg-blue-50 text-blue-800 border border-blue-200 flex-shrink-0">
              <ShieldCheck className="w-3 h-3" />DB override active
            </span>
          )}
        </div>

        {overridden && (
          <div className="text-xs text-gray-500 mb-3 bg-gray-50 rounded p-2 font-mono">
            <span className="text-gray-700">{current.prefix}</span>
            <span className="text-gray-400">{"•".repeat(Math.max(0, Math.min(20, current.len - 16)))}</span>
            <span className="text-gray-700">{current.suffix}</span>
            <span className="text-[10px] text-gray-400 ml-2">({current.len} chars)</span>
            {current.updated_at && (
              <div className="text-[10px] text-gray-400 mt-1 font-sans">
                Last updated: {new Date(current.updated_at).toLocaleString()}
              </div>
            )}
          </div>
        )}

        <div className="flex items-stretch gap-2">
          <div className="relative flex-1">
            <Input
              type={showValue ? "text" : "password"}
              autoComplete="new-password"
              spellCheck="false"
              placeholder={meta.placeholder}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="pr-9 font-mono text-xs"
              data-testid={`input-${keyName}`}
            />
            <button
              type="button"
              onClick={() => setShowValue(!showValue)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              tabIndex={-1}
              aria-label={showValue ? "Hide value" : "Show value"}
            >
              {showValue ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving || !value.trim()}
            size="sm"
            className="bg-black text-white hover:bg-gray-800 hover:text-white"
            data-testid={`save-${keyName}`}
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save override"}
          </Button>
          {overridden && (
            <Button
              onClick={handleDelete}
              disabled={deleting}
              size="sm"
              variant="outline"
              className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              data-testid={`delete-${keyName}`}
              title="Remove override and fall back to env var"
            >
              {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
